import {
  AspectRatio,
  aspectRatios,
  aspectRatioValues,
} from '@tv2/lib-offspring/libs/image/aspectRatiosUtils';
import { toImgix } from '@tv2/lib-offspring/libs/image/imgix';
import classNames from 'classnames';

import { memo, useEffect, useRef, useState } from 'react';
import { SimpleLazyImage } from '../primitives/SimpleLazyImage';

type ImgixLazyImageProps = {
  className?: string;
  alt?: string;
  src: string;
  width?: number;
  height?: number;
  imgixOverrides?: Record<string, any>;
  aspectRatio?: AspectRatio;
  zoomImage?: boolean;
  padLeft?: number;
  onLoad?: () => void;
};

export const ImgixLazyImage = memo(
  ({
    className,
    alt,
    src,
    width,
    height,
    padLeft,
    imgixOverrides = {},
    aspectRatio = aspectRatios.default,
    zoomImage = false,
    onLoad,
  }: ImgixLazyImageProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [w, setWidth] = useState(width);
    const [ready, setReady] = useState(Boolean(width));
    const [url, setUrl] = useState('');
    const [loaded, setLoaded] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency management
    useEffect(() => {
      if (ref.current && !height && !width) {
        const { clientWidth }: any = ref.current;
        if (clientWidth) setWidth(clientWidth);
      }
      setReady(true);
    }, [height, width, setWidth, w, setReady]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency management
    useEffect(() => {
      if (ready) {
        setUrl(
          toImgix(src ?? '', {
            ar: aspectRatio,
            auto: ['format', 'compress'],
            dpr: 2,
            w: w !== undefined ? Math.max(w, 256) : w,
            padLeft,
            ...imgixOverrides,
          })
        );
      }
    }, [ready, setUrl, src, w, imgixOverrides, aspectRatio, padLeft]);

    const computeSize = !width || !height;
    const innerStyles = computeSize
      ? { paddingBottom: `${aspectRatioValues[aspectRatio]}%` }
      : { width: `${width}px`, height: `${height}px` };
    const onLoadFnc = () => {
      setLoaded(true);
      onLoad?.();
    };
    return (
      <div
        style={innerStyles}
        className={classNames(
          'relative overflow-hidden ease-in-out',
          'transform transition-transform duration-[200ms] linear',
          { 'scale-125 rotate-[0.01deg]': ready && zoomImage }
        )}
        ref={ref}
      >
        <SimpleLazyImage
          w={w}
          className={classNames(
            className,
            'absolute transition-opacity duration-200 ease-in-out opacity-0',
            { 'opacity-100': loaded }
          )}
          url={url}
          alt={alt}
          onLoad={onLoadFnc}
        ></SimpleLazyImage>
      </div>
    );
  }
);
